import { useMemo } from 'react'
import moment, { type Moment } from 'moment'
import {
  type MetricThresholdInput,
  MetricType,
  useGetPickupAggregateHistoricalStorePerformanceMeshQuery,
} from 'domains/aggregate-store-performance/api'
import { useRetailerId } from 'utils/routing/params.hooks'
import {
  calculateTrend,
  determineTrendDirection,
} from 'gin-and-tonic/containers/picking-performance-overview/'
import {
  type HistoricalStorePerformanceOverviewMetrics,
  type StoreMetric,
  type Trend,
} from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'
import {
  metricTypeToFieldName,
  metricTypeToFieldTrend,
} from 'domains/aggregate-store-performance/utils/constants/AggregateStorePerformanceOverviewPage.constants'
import {
  getEndOfWeek,
  toQueryDateFormat,
  responseMetricToNumber,
  isResponseMetricDefined,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import { type FiltersDropdownOption } from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.types'
import { MetricFilters } from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.constants'

const filtersDropdownOptionsToMetricInputThresholds = (
  filters: FiltersDropdownOption[]
): MetricThresholdInput[] => {
  const filterToMetricThreshold: Record<MetricFilters, MetricThresholdInput> = {
    [MetricFilters.Availability]: {
      metricType: MetricType.FiveHourAvailabilityRate,
      threshold: '80',
    },
    [MetricFilters.Utilization]: {
      metricType: MetricType.DedicatedUtilizationRate,
      threshold: '70',
    },
    [MetricFilters.MatchRate]: {
      metricType: MetricType.MatchRate,
      threshold: '80',
    },
  }

  return filters.map(filter => filterToMetricThreshold[filter.value])
}

interface useGetPickupStorePerformanceMetricsOptions {
  filters: FiltersDropdownOption[]
  warehouseLocationIds: string[]
  startDate: Moment
  isWeek: boolean
}

export const useGetPickupStoreHistoricalPerformanceMetrics = ({
  filters,
  warehouseLocationIds,
  startDate,
  isWeek,
}: useGetPickupStorePerformanceMetricsOptions) => {
  const retailerId = useRetailerId()
  const endDate = isWeek ? getEndOfWeek(startDate) : startDate
  const shouldSkip = !warehouseLocationIds?.length

  const metricThresholdInputs = useMemo(
    () => filtersDropdownOptionsToMetricInputThresholds(filters),
    [filters]
  )

  const { data, loading, error } = useGetPickupAggregateHistoricalStorePerformanceMeshQuery({
    variables: {
      metricThresholds: metricThresholdInputs,
      warehouseId: retailerId,
      warehouseLocationIds,
      startDate: toQueryDateFormat(startDate),
      endDate: toQueryDateFormat(endDate),
      devicePrioritizationEnabled: true,
    },
    skip: shouldSkip,
  })

  const updatedAt =
    data?.historicalStorePerformanceServiceGetAggregatedHistoricalStorePerformance?.updatedAt ??
    undefined

  const metrics =
    data?.historicalStorePerformanceServiceGetAggregatedHistoricalStorePerformance?.metrics ?? []

  const perStoreMetrics: StoreMetric[] = metrics.map(metric => ({
    storeId: metric.warehouseLocationId,
    storeName: metric.name,
    scheduleGaps: metric.scheduledGaps,
    hoursRecommended: responseMetricToNumber(metric.recommendedHours),
    hoursScheduled: responseMetricToNumber(metric.scheduledHours),
    fiveHourAvailability: responseMetricToNumber(metric.fiveHourAvailabilityRate),
    partnerPickOrders: metric.partnerPickOrdersCount,
    fullServiceShopperOrders: metric.fssOrdersCount,
    dedicatedMatchRate: responseMetricToNumber(metric.dedicatedMatchRate),
    dedicatedUtilization: responseMetricToNumber(metric.dedicatedUtilizationRate),
  }))

  const overviewMetrics =
    data?.historicalStorePerformanceServiceGetAggregatedHistoricalStorePerformance
      ?.overviewMetrics ?? []

  const aggregateStoreMetrics: HistoricalStorePerformanceOverviewMetrics = overviewMetrics.reduce(
    (acc, metric) => {
      if (metricTypeToFieldName[metric.metricType] === undefined) {
        return acc
      }

      if (
        metricTypeToFieldTrend[metric.metricType] &&
        isResponseMetricDefined(metric.currentValue)
      ) {
        const currentValue = responseMetricToNumber(metric.currentValue, false)
        const pastValue = responseMetricToNumber(metric.pastValue, false)

        const trendValue = calculateTrend(currentValue, pastValue)

        const trend: Trend = {
          value: Number(trendValue.toFixed(1)),
          direction: determineTrendDirection(trendValue),
        }

        acc[metricTypeToFieldTrend[metric.metricType]] = trend
      }

      acc[metricTypeToFieldName[metric.metricType]] = responseMetricToNumber(metric.currentValue)

      return acc
    },
    {}
  )

  const scheduledHours = overviewMetrics.find(
    metric => metric.metricType === MetricType.ScheduledHours
  )
  const recommendedHours = overviewMetrics.find(
    metric => metric.metricType === MetricType.RecommendedHours
  )

  if (scheduledHours !== undefined && recommendedHours !== undefined) {
    const pastTotalHours =
      responseMetricToNumber(scheduledHours.pastValue, false) /
      responseMetricToNumber(recommendedHours.pastValue, false)
    const currentTotalHours =
      responseMetricToNumber(scheduledHours.currentValue, false) /
      responseMetricToNumber(recommendedHours.currentValue, false)

    const totalHoursTrendValue = calculateTrend(currentTotalHours, pastTotalHours)

    const totalHoursTrend: Trend = {
      value: totalHoursTrendValue,
      direction: determineTrendDirection(totalHoursTrendValue),
    }

    aggregateStoreMetrics.totalHoursTrend = totalHoursTrend
  }

  return {
    updatedAt: updatedAt ? moment(updatedAt) : undefined,
    perStoreMetrics,
    aggregateStoreMetrics,
    loading,
    error,
  }
}
